import { CannedQuestionName } from '../../../api/models/survey';
import { FormSurveyAnswer } from '../../../models/forms';
import {
    AttendeeSurvey,
    FormAnswer,
    shouldIncludeInBasicInfo,
    shouldIncludeInBuyerQuestions,
    shouldIncludeInPaymentQuestions,
    SurveyFormData,
    SurveyQuestion,
} from '../../../models/survey';
import { CheckoutState } from '../../../state';
import { isCannedQuestion, isRequiredQuestion } from '../../../utils/surveys';
import { getAllButRequiredWaiverQuestionsForAttendees } from './getAllButRequiredWaiverQuestionsForAttendees';
import { getAllButRequiredWaiverQuestionsForBuyer } from './getAllButRequiredWaiverQuestionsForBuyer';
import { getAnswersPayloadForBackendFactory } from './getAnswersPayloadForBackendFactory';
import { getCannedAndOptionalQuestionsForAttendees } from './getCannedAndOptionalQuestionsForAttendees';
import { getCannedAndOptionalQuestionsForBuyer } from './getCannedAndOptionalQuestionsForBuyer';
import { getCleanedFormDataFactory } from './getCleanedFormDataFactory';
import { getHasInventoryQuestions } from './getHasInventoryQuestions';
import { getHasQuebecQSTTaxQuestion } from './getHasQuebecQSTTaxQuestion';
import { getHasTaxQuestions } from './getHasTaxQuestions';
import {
    getHasRequiredWaiverQuestions,
    getHasWaiverQuestions,
} from './getHasWaiverQuestions';
import { getOptionalQuestionsForAttendees } from './getOptionalQuestionsForAttendees';
import { getOptionalQuestionsForBuyer } from './getOptionalQuestionsForBuyer';
import { getOriginalAnswersFactory } from './getOriginalAnswersFactory';
import { getQuestionsForAttendees } from './getQuestionsForAttendees';
import { getQuestionsForBuyer } from './getQuestionsForBuyer';
import {
    getStatisticsFactory,
    SingleFormSurveyStatistics,
} from './getStatisticsFactory';
import {
    getSurveyDistribution,
    SurveyDistribution,
} from './getSurveyDistribution';

export interface SingleFormSurveySettings {
    buyer: {
        questions: {
            all: Array<SurveyQuestion>;
            basicInfo: Array<SurveyQuestion>;
            organizer: Array<SurveyQuestion>;
            payment: Array<SurveyQuestion>;
        };
    };
    attendees: {
        questions: Array<SurveyQuestion>;
        surveys: Array<AttendeeSurvey>;
    };
    hasRequiredQuestions: boolean;
    hasQuestions: boolean;
    getStatistics: (formData: SurveyFormData) => SingleFormSurveyStatistics;
    getCleanedFormData: (formData: SurveyFormData) => SurveyFormData;
    getAnswersPayloadForBackend: (
        formData: SurveyFormData,
    ) => Array<FormSurveyAnswer>;
    getOriginalAnswers: (state: CheckoutState) => Array<{
        attendeeId: string;
        question: SurveyQuestion;
        answer: FormAnswer;
    }>;
}

const getHasRequiredQuestions = ({
    questionsForBuyer,
    attendeeSurveys,
}: {
    questionsForBuyer: Array<SurveyQuestion>;
    attendeeSurveys: Array<AttendeeSurvey>;
}) =>
    questionsForBuyer.some((question) =>
        isRequiredQuestion(question, questionsForBuyer),
    ) ||
    attendeeSurveys.some(({ survey }) =>
        survey.some((question) => isRequiredQuestion(question, survey)),
    );

const getSingleFormSurveySettings = ({
    attendeeSurveys,
    questionsForAttendee,
    questionsForBuyer,
}: {
    attendeeSurveys: Array<AttendeeSurvey>;
    questionsForAttendee: Array<SurveyQuestion>;
    questionsForBuyer: Array<SurveyQuestion>;
}): SingleFormSurveySettings => {
    const basicInfoQuestions = questionsForBuyer.filter(({ name }) =>
        shouldIncludeInBasicInfo(name),
    );
    const paymentQuestions = questionsForBuyer.filter(({ name }) =>
        shouldIncludeInPaymentQuestions(name),
    );
    const organizerQuestionsForBuyer = questionsForBuyer.filter(({ name }) =>
        shouldIncludeInBuyerQuestions(name),
    );
    const billingInfoQuestions = questionsForBuyer.filter(
        ({ name }) => name === CannedQuestionName.bill,
    );

    const hasRequiredQuestions = getHasRequiredQuestions({
        questionsForBuyer: organizerQuestionsForBuyer,
        attendeeSurveys,
    });

    const hasQuestions =
        questionsForAttendee.length + organizerQuestionsForBuyer.length > 0;

    const getStatistics = getStatisticsFactory({
        attendeeSurveys,
        questionsForBuyer: organizerQuestionsForBuyer,
    });

    const getCleanedFormData = getCleanedFormDataFactory({
        attendeeSurveys,
        questionsForBuyer,
    });

    const getAnswersPayloadForBackend = getAnswersPayloadForBackendFactory({
        attendeeSurveys,
        questionsForBuyer: [
            // Basic info questions are just regular buyer questions that are
            // displayed in its own section of the form. However, payment
            // questions are special questions whose answer is sent to a
            // different endpoint with a different payload format so we don't
            // want them as part of the return value of this function
            ...basicInfoQuestions,
            ...organizerQuestionsForBuyer,
            ...billingInfoQuestions,
        ],
    });

    const getOriginalAnswers = getOriginalAnswersFactory({
        questionsForAttendee,
        questionsForBuyer,
    });

    return {
        attendees: {
            surveys: attendeeSurveys,
            questions: questionsForAttendee,
        },
        buyer: {
            questions: {
                all: questionsForBuyer,
                basicInfo: basicInfoQuestions,
                payment: paymentQuestions,
                organizer: organizerQuestionsForBuyer,
            },
        },
        hasRequiredQuestions,
        hasQuestions,
        getStatistics,
        getCleanedFormData,
        getAnswersPayloadForBackend,
        getOriginalAnswers,
    };
};

const getAllQuestions = (state: CheckoutState): SingleFormSurveySettings => {
    const attendeeSurveys = getQuestionsForAttendees(state);
    const questionsForAttendee = attendeeSurveys.flatMap(
        ({ survey }) => survey,
    );
    const questionsForBuyer = getQuestionsForBuyer(state);

    return getSingleFormSurveySettings({
        attendeeSurveys,
        questionsForAttendee,
        questionsForBuyer,
    });
};

const getPostPaymentQuestionsBySurveyDistribution: Record<
    SurveyDistribution,
    (state: CheckoutState) => {
        attendeeSurveys: Array<AttendeeSurvey>;
        questionsForBuyer: Array<SurveyQuestion>;
    }
> = {
    [SurveyDistribution.allQuestionsAfterPayment]: (state) => ({
        attendeeSurveys: getQuestionsForAttendees(state),
        questionsForBuyer: getQuestionsForBuyer(state),
    }),
    [SurveyDistribution.cannedQuestionsAndOptionalQuestionsAfterPayment]: (
        state,
    ) => ({
        attendeeSurveys: getCannedAndOptionalQuestionsForAttendees(state),
        questionsForBuyer: getCannedAndOptionalQuestionsForBuyer(state),
    }),
    [SurveyDistribution.onlyOptionalQuestionsAfterPayment]: (state) => ({
        attendeeSurveys: getOptionalQuestionsForAttendees(state),
        questionsForBuyer: getOptionalQuestionsForBuyer(state),
    }),
    [SurveyDistribution.allButRequiredInventoryAndRequiredWaiverQuestionsAfterPayment]:
        (state) => ({
            attendeeSurveys:
                getAllButRequiredWaiverQuestionsForAttendees(state),
            questionsForBuyer: getAllButRequiredWaiverQuestionsForBuyer(state),
        }),
};

const getPostPaymentQuestions = (
    state: CheckoutState,
): SingleFormSurveySettings => {
    const surveyDistribution = getSurveyDistribution(state);
    const getQuestions =
        getPostPaymentQuestionsBySurveyDistribution[surveyDistribution];
    const { attendeeSurveys, questionsForBuyer } = getQuestions(state);

    const questionsForAttendee = attendeeSurveys.flatMap(
        ({ survey }) => survey,
    );

    return getSingleFormSurveySettings({
        attendeeSurveys,
        questionsForAttendee,
        questionsForBuyer: questionsForBuyer.filter(
            ({ name }) => !shouldIncludeInBasicInfo(name),
        ),
    });
};

export const getSurvey = (
    state: CheckoutState,
): {
    hasInventoryQuestions: boolean;
    hasIsBusinessQuestion: boolean;
    hasOptionalQuestions: boolean;
    hasQuebecQSTTaxQuestion: boolean;
    hasRequiredCannedQuestions: boolean;
    hasRequiredCustomQuestions: boolean;
    hasRequiredQuestions: boolean;
    hasTaxQuestions: boolean;
    hasWaiverQuestions: boolean;
    hasRequiredWaiverQuestions: boolean;
    preTicketsSurveySettings: SingleFormSurveySettings;
    payinsSurveySettings: SingleFormSurveySettings;
    preOrderConfirmationSurveySettings: SingleFormSurveySettings;
    prePaymentSurveySettings: SingleFormSurveySettings;
} => {
    const {
        attendees: {
            questions: allQuestionsForAttendee,
            surveys: allAttendeesSurveys,
        },
        buyer: {
            questions: { all: allQuestionsForBuyer },
        },
    } = getAllQuestions(state);
    const {
        attendees: {
            questions: postPaymentQuestionsForAttendee,
            surveys: postPaymentAttendeesSurveys,
        },
        buyer: {
            questions: { all: postPaymentQuestionsForBuyer },
        },
    } = getPostPaymentQuestions(state);

    const allQuestions = [...allQuestionsForAttendee, ...allQuestionsForBuyer];

    const postPaymentQuestionsByAttendeeNames = postPaymentAttendeesSurveys.map(
        ({ survey }) => new Set(survey.map(({ name }) => name)),
    );
    const postPaymentQuestionsForBuyerNames = new Set(
        postPaymentQuestionsForBuyer.map(({ name }) => name),
    );

    const prePaymentAttendeesSurveys = allAttendeesSurveys.map(
        ({ survey, ...metadata }, index) => {
            const questionsNotAskedPostPayment = survey.filter(
                ({ name }) =>
                    !postPaymentQuestionsByAttendeeNames[index].has(name),
            );

            return {
                ...metadata,
                survey: questionsNotAskedPostPayment,
            };
        },
    );
    const prePaymentQuestionsForAttendee = prePaymentAttendeesSurveys.flatMap(
        ({ survey }) => survey,
    );
    const prePaymentQuestionsForBuyer = allQuestionsForBuyer.filter(
        ({ name }) => !postPaymentQuestionsForBuyerNames.has(name),
    );

    const optionalQuestions = allQuestions.filter(
        (question) => !isRequiredQuestion(question, allQuestions),
    );

    const requiredQuestions = allQuestions.filter((question) =>
        isRequiredQuestion(question, allQuestions),
    );

    const hasRequiredCannedQuestions = requiredQuestions.some((question) =>
        isCannedQuestion(question),
    );
    const hasRequiredCustomQuestions = requiredQuestions.some(
        (question) => !isCannedQuestion(question),
    );

    const hasOptionalQuestions = optionalQuestions.length > 0;
    const hasRequiredQuestions = requiredQuestions.length > 0;

    const prePaymentSurveySettings = getSingleFormSurveySettings({
        attendeeSurveys: prePaymentAttendeesSurveys,
        questionsForAttendee: prePaymentQuestionsForAttendee,
        questionsForBuyer: prePaymentQuestionsForBuyer,
    });

    const preOrderConfirmationSurveySettings = getSingleFormSurveySettings({
        attendeeSurveys: postPaymentAttendeesSurveys,
        questionsForAttendee: postPaymentQuestionsForAttendee,
        questionsForBuyer: postPaymentQuestionsForBuyer,
    });

    const allSurveySettings = getSingleFormSurveySettings({
        attendeeSurveys: allAttendeesSurveys,
        questionsForAttendee: allQuestionsForAttendee,
        questionsForBuyer: allQuestionsForBuyer,
    });

    const hasIsBusinessQuestion = allQuestionsForBuyer.some(
        ({ name }) => name === CannedQuestionName.isBusiness,
    );
    const hasTaxQuestions = getHasTaxQuestions(state);
    const hasQuebecQSTTaxQuestion = getHasQuebecQSTTaxQuestion(state);
    const hasInventoryQuestions = getHasInventoryQuestions(state);
    const hasWaiverQuestions = getHasWaiverQuestions(state);
    const hasRequiredWaiverQuestions = getHasRequiredWaiverQuestions(state);

    return {
        hasInventoryQuestions,
        hasIsBusinessQuestion,
        hasOptionalQuestions,
        hasQuebecQSTTaxQuestion,
        hasRequiredCannedQuestions,
        hasRequiredCustomQuestions,
        hasRequiredQuestions,
        hasTaxQuestions,
        hasWaiverQuestions,
        hasRequiredWaiverQuestions,
        preTicketsSurveySettings: allSurveySettings,
        payinsSurveySettings: allSurveySettings,
        preOrderConfirmationSurveySettings,
        prePaymentSurveySettings,
    };
};
